import React, { useEffect, useMemo, useState } from 'react';
import { tenantAdminApi } from '@/utils/tenantAdminApi';
import { rbacApi } from '@/modules/admin-rbac/api/rbac.api';
import InviteUserDrawer from '@/modules/admin-rbac/components/InviteUserDrawer';
import UserProfileDrawer from '@/modules/admin-rbac/components/UserProfileDrawer';
import StaffProfileView from '@/modules/admin-rbac/components/StaffProfileView';
import UsersTable from '@/modules/admin-rbac/components/UsersTable';
import { isActiveUser } from '@/modules/admin-rbac/utils/userProfileForm';
import useInLeadershipShell from '@/hooks/useInLeadershipShell';
import LeadershipPageHeader from '@/components/LeadershipPageHeader';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';
import { toast } from 'sonner';

export default function UsersPage() {
  const lead = useInLeadershipShell();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [editUser, setEditUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, r, s] = await Promise.all([
        tenantAdminApi.listUsers(),
        rbacApi.loadRoles(true),
        rbacApi.loadSpecialisations(true),
      ]);
      setUsers(u);
      setRoles(r);
      setSpecs(s);
    } catch (_) {
      toast.error('Failed to load users');
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const activeUsers = useMemo(() => users.filter(isActiveUser), [users]);
  const deactivatedUsers = useMemo(() => users.filter((u) => !isActiveUser(u)), [users]);
  const visible = tab === 'active' ? activeUsers : deactivatedUsers;

  const roleLabel = (u) => (
    u.role_name
    || roles.find((r) => r.role_key === u.clinic_role_key)?.name
    || '—'
  );

  const patchLocal = (updated) => {
    setUsers((list) => list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setEditUser((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
  };

  const changeClinicRole = async (u, role_key) => {
    try {
      patchLocal(await tenantAdminApi.updateUser(u.id, { role_key }));
      toast.success('Role updated');
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed');
    }
  };

  const changeSpec = async (u, specialization) => {
    try {
      patchLocal(await tenantAdminApi.updateUser(u.id, { specialization }));
      toast.success('Specialisation updated');
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed');
    }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}? They will not be able to log in.`)) return;
    try {
      patchLocal(await tenantAdminApi.deactivateUser(u.id));
      toast.success('User deactivated');
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed');
    }
  };

  const reactivate = async (u) => {
    try {
      patchLocal(await tenantAdminApi.reactivateUser(u.id));
      toast.success('User reactivated');
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed');
    }
  };

  const tabCls = (id) =>
    `px-4 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition ${
      tab === id
        ? 'border-[#F15A22] text-[#071B34] bg-white'
        : 'border-transparent text-zinc-500 hover:text-zinc-800'
    }`;

  return (
    <div className={`${lead ? `${LEADERSHIP_CONTENT} space-y-5` : 'p-6 max-w-6xl mx-auto space-y-5'}`} data-testid="ta-users-page">
      {lead ? (
        <LeadershipPageHeader
          title="Users"
          subtitle="Staff directory with profiles. Deactivate instead of delete — blocked from login."
          actions={<InviteUserDrawer onDone={refresh} />}
        />
      ) : (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 font-heading" data-testid="ta-users-title">
              Users
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Doctor directory with profiles. Deactivate instead of delete — blocked from login.
            </p>
          </div>
          <InviteUserDrawer onDone={refresh} />
        </div>
      )}

      <div className="flex gap-1 border-b" data-testid="ta-users-tabs">
        <button type="button" className={tabCls('active')} onClick={() => setTab('active')}>
          Active ({activeUsers.length})
        </button>
        <button type="button" className={tabCls('deactivated')} onClick={() => setTab('deactivated')}>
          Deactivated ({deactivatedUsers.length})
        </button>
      </div>

      <UsersTable
        users={visible}
        roles={roles}
        specs={specs}
        loading={loading}
        roleLabel={roleLabel}
        deactivated={tab === 'deactivated'}
        onChangeRole={changeClinicRole}
        onChangeSpec={changeSpec}
        onView={setViewUser}
        onEdit={setEditUser}
        onDeactivate={deactivate}
        onReactivate={reactivate}
      />

      <StaffProfileView
        userId={viewUser?.id}
        roleLabel={viewUser ? roleLabel(viewUser) : ''}
        open={Boolean(viewUser)}
        onClose={() => setViewUser(null)}
        onEdit={(u) => { setViewUser(null); setEditUser(u); }}
      />

      <UserProfileDrawer
        user={editUser}
        specs={specs}
        open={Boolean(editUser)}
        onClose={() => setEditUser(null)}
        onSaved={patchLocal}
      />
    </div>
  );
}
